import { ICellEditorParams, RowNode } from 'ag-grid-community';
import { FieldmagicRelateInputFilter, FieldmagicRelateInputValue } from '../forms/input/relate/relate-input.component';
import {
  blank,
  data_get,
  data_is_true,
  data_nullify,
  fallback,
  FieldmagicNativeDateTime,
  filled,
  generate_id,
  is_callable,
  observe,
  OptionalValue,
  spf,
  str_len,
  str_snake_case,
  str_trim,
  str_truncate,
  when,
} from '../../utils/common';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { BehaviorSubject, concat, Observable, of, Subject, Subscription } from 'rxjs';
import { FieldmagicRelateOption, RelateSearchService } from '../../../services/relate-search.service';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { FieldmagicDropdownOption } from '../forms/input/dropdown/dropdown.component';
import { Component, OnDestroy } from '@angular/core';
import {
  FieldmagicDataGridConfiguredDropdownOption,
  FieldmagicDataGridDropdownService,
} from '../../../services/data-grid-services';
import { difference, to_numeric } from '../../utils/numbers';

export type FieldmagicDataGridInputConfig = {
  placeholder?: string;
  tooltip?: string;
  enable_mass_update?: boolean;
};

@Component({
  selector: 'fieldmagic-data-grid-dropdown-cell-input',
  template: `
    <ng-select
      [(ngModel)]="value"
      [items]="options$ | async"
      appendTo="body"
      bindLabel="text"
      class="fieldmagic-data-grid-dropdown"
      [clearable]="clearable$ | async"
      [multiple]="multi$ | async"
      [placeholder]="placeholder$ | async | translate"
      [matTooltip]="tooltip$ | async"
      (ngModelChange)="onChanged()"
    >
      <!-- displayed for the selected dropdown option -->
      <ng-template
        ng-label-tmp
        let-item="item"
        let-clear="clear"
      >
        <span class="ng-value-label">{{ item.text | translate }}</span>
        <span
          *ngIf="multi$ | async"
          class="ng-value-icon right selected-close"
          (click)="clear(item)"
          innerText="x"
        >
        </span>
      </ng-template>

      <!-- displayed for the dropdown options -->
      <ng-template
        ng-option-tmp
        let-item="item"
      >
        <span class="ng-value-label">{{ item.text | translate }}</span>
      </ng-template>

      <!-- displayed for results found: only when customizable is enabled -->
      <ng-template
        let-searchTerm="searchTerm"
        ng-notfound-tmp
      >
        <span
          *ngIf="(customizable$ | async) && (searchTerm | filled)"
          class="ng-option"
          (click)="onNewCustomizedOption(searchTerm)"
        >
          {{ 'Add new item for "%s"' | translate | sprintf : [searchTerm] }}
        </span>

        <span
          *ngIf="searchTerm | blank"
          class="ng-option disabled"
          [innerText]="'No items found' | translate"
        >
        </span>
      </ng-template>
    </ng-select>
  `,
  providers: [
    FieldmagicDataGridDropdownService,
  ],
  styles: [
    `
      .fieldmagic-data-grid-dropdown ::ng-deep .ng-select-container {
        min-height: 40px !important;
        font-size: 12px;
      }
    `,
    `
      .fieldmagic-data-grid-dropdown ::ng-deep .ng-select-container .ng-value-icon {
        border-left: 0px !important;
      }
    `,
    `
      .fieldmagic-data-grid-dropdown.ng-select-filtered ::ng-deep .ng-select-container .ng-clear-wrapper,
      .fieldmagic-data-grid-dropdown.ng-select-filtered ::ng-deep .ng-select-container .ng-arrow-wrapper,
      .fieldmagic-data-grid-dropdown.ng-select-filtered ::ng-deep .ng-value-container .ng-value-label,
      .fieldmagic-data-grid-dropdown.ng-select-filtered ::ng-deep .ng-placeholder,
      .fieldmagic-data-grid-dropdown.ng-select-opened ::ng-deep .ng-select-container .ng-clear-wrapper,
      .fieldmagic-data-grid-dropdown.ng-select-opened ::ng-deep .ng-select-container .ng-arrow-wrapper,
      .fieldmagic-data-grid-dropdown.ng-select-opened ::ng-deep .ng-placeholder,
      .fieldmagic-data-grid-dropdown.ng-select-opened ::ng-deep .ng-value-container .ng-value-label,
      .fieldmagic-data-grid-dropdown.ng-touched ::ng-deep .ng-select-container .ng-clear-wrapper,
      .fieldmagic-data-grid-dropdown.ng-touched ::ng-deep .ng-select-container .ng-arrow-wrapper,
      .fieldmagic-data-grid-dropdown.ng-touched ::ng-deep .ng-value-container .ng-value-label,
      .fieldmagic-data-grid-dropdown.ng-touched ::ng-deep .ng-placeholder {
        top: 6px;
        position: relative;
      }
    `,
  ],
})
export class FieldmagicDataGridDropdownCellInputComponent
  implements ICellEditorAngularComp, OnDestroy
{
  value?: OptionalValue<FieldmagicDataGridConfiguredDropdownOption | FieldmagicDropdownOption>;

  readonly options$ = new BehaviorSubject<
    (FieldmagicDataGridConfiguredDropdownOption | FieldmagicDropdownOption)[]
  >([]);
  readonly fetching$ = new BehaviorSubject<boolean>(false);

  /// options
  readonly placeholder$ = new BehaviorSubject<OptionalValue<string>>(null);
  readonly clearable$ = new BehaviorSubject<boolean>(true);
  readonly multi$ = new BehaviorSubject<boolean>(false);
  readonly customizable$ = new BehaviorSubject<boolean>(false);
  readonly tooltip$ = new BehaviorSubject<OptionalValue<string>>(null);
  /// end options

  private readonly remote$ = new BehaviorSubject<
    OptionalValue<FieldmagicDataGridDropdownInputConfig['remote_config']>
  >(null);

  private readonly _params$ = new BehaviorSubject<OptionalValue<ICellEditorParams>>(null);

  /// subs
  private _onDropdownOptionFetching$?: Subscription;
  /// end subs

  constructor(private readonly _dropdowns: FieldmagicDataGridDropdownService) {}

  ngOnDestroy(): void {
    if (this._onDropdownOptionFetching$) {
      this._onDropdownOptionFetching$.unsubscribe();
    }
  }

  agInit(params: ICellEditorParams): void {
    this.value = data_nullify(params.value);

    const metadata: FieldmagicDataGridDropdownInputConfig = Object.assign(
      {
        clearable: true,
        multi: false,
        customizable: false,
      },
      params.colDef.cellEditorParams(params),
    );

    this.tooltip$.next(metadata.tooltip);

    if (filled(data_get(metadata, 'remote_config'))) {
      this._onDropdownOptionFetching$ = observe({
        before: () => this.fetching$.next(true),
        after: () => this.fetching$.next(false),
        observable: () =>
          this._dropdowns.getOptions({
            module_name: metadata.remote_config.module_name,
            field_name: metadata.remote_config.field_name,
          }),
      }).subscribe((options) =>
        this.options$.next(
          fallback(options, {
            to: () => [],
          }),
        ),
      );

      this.remote$.next(metadata.remote_config);
    }

    if (filled(data_get(metadata, 'static_options'))) {
      this.options$.next(metadata.static_options);
    }

    this.placeholder$.next(data_nullify(data_get(metadata, 'placeholder')));
    this.clearable$.next(metadata.clearable);
    this.multi$.next(metadata.multi);
    this.customizable$.next(metadata.customizable);
    this._params$.next(params);
  }

  getValue = () => this.value;

  onNewCustomizedOption(term: OptionalValue<string>): void {
    if (blank(term)) {
      return;
    }

    term = str_truncate(str_trim(term), {
      max_length: 64,
    });

    const option: FieldmagicDropdownOption = {
      id: str_snake_case(term, {
        lowercase: true,
      }),
      text: term,
    };

    this.value = option;

    if (blank(this.remote$.value)) {
      return this.options$.next([...this.options$.value, ...[option]]);
    }

    const metadata = this.remote$.value;

    this._dropdowns.addOptions({
      module_name: metadata.module_name,
      field_name: metadata.field_name,
      options: {
        id: option.id,
        text: option.text,
        position: this.options$.value.length - 1,
      },
    });

    return this.options$.next([...this.options$.value, ...[option]]);
  }

  onChanged = () => _update_data_node_value(this.value, {
    params: this._params$.value,
  });
}

export type FieldmagicDataGridDropdownInputConfig = FieldmagicDataGridInputConfig & {
  remote_config?: {
    module_name: string;
    field_name: string;
  };
  static_options?: FieldmagicDropdownOption[];
  editable?: boolean;
  clearable?: boolean;
  multi?: boolean;
  customizable?: boolean;
  bind_value?: string;
};

@Component({
  selector: 'fieldmagic-data-grid-relate-dropdown-cell-input',
  template: `
    <ng-container
      *ngIf="useText$ | async; else relate;"
      [ngTemplateOutlet]="text"
    >
    </ng-container>

    <ng-template #relate>
      <ng-select
        [(ngModel)]="relateValue"
        [items]="options$ | async"
        appendTo="body"
        bindLabel="text"
        class="fieldmagic-data-grid-relate-dropdown"
        [clearable]="clearable$ | async"
        [multiple]="multi$ | async"
        [placeholder]="placeholder$ | async | translate"
        [typeahead]="typeahead$"
        [matTooltip]="tooltip$ | async"
        (ngModelChange)="onChanged()"
        (open)="onOpen()"
      >
        <!-- displayed for the selected dropdown option -->
        <ng-template
          ng-label-tmp
          let-item="item"
          let-clear="clear"
        >
          <span class="ng-value-label">{{ item.text | translate }}</span>
          <span
            *ngIf="multi$ | async"
            class="ng-value-icon right selected-close"
            (click)="clear(item)"
            innerText="x"
          >
          </span>
        </ng-template>

        <!-- displayed for the dropdown options -->
        <ng-template
          ng-option-tmp
          let-item="item"
        >
          <span class="ng-value-label">{{ item.text | translate }}</span>
        </ng-template>
      </ng-select>
    </ng-template>

    <ng-template #text>
      <div class="w-100 h-100 d-flex p-2 flex-column">
        <textarea
          rows="10"
          cols="60"
          class="form-control font-size-11 fieldmagic-data-grid-textarea-input"
          [(ngModel)]="textValue"
          [attr.maxLength]="max"
          [matTooltip]="tooltip$ | async"
          [attr.placeholder]="placeholder$ | async"
          (ngModelChange)="onChanged()"
          (keydown)="onKeyDown($event)"
          autofocus
        >
        </textarea>

        <div
          *ngIf="max | filled"
          class="d-flex w-100 justify-content-end"
        >
          <small class="text-help">
            {{ '%d/%d' | sprintf: [remaining$ | async, max]}}
          </small>
        </div>
      </div>
    </ng-template>
  `,
  styles: [
    `
      .fieldmagic-data-grid-relate-dropdown ::ng-deep .ng-select-container {
        min-height: 40px !important;
        font-size: 12px;
      }
    `,
    `
      .fieldmagic-data-grid-relate-dropdown ::ng-deep .ng-select-container .ng-value-icon {
        border-left: 0px !important;
      }
    `,
    `
      .fieldmagic-data-grid-relate-dropdown.ng-select-filtered ::ng-deep .ng-select-container .ng-clear-wrapper,
      .fieldmagic-data-grid-relate-dropdown.ng-select-filtered ::ng-deep .ng-select-container .ng-arrow-wrapper,
      .fieldmagic-data-grid-relate-dropdown.ng-select-filtered ::ng-deep .ng-value-container .ng-value-label,
      .fieldmagic-data-grid-relate-dropdown.ng-select-filtered ::ng-deep .ng-placeholder,
      .fieldmagic-data-grid-relate-dropdown.ng-select-opened ::ng-deep .ng-select-container .ng-clear-wrapper,
      .fieldmagic-data-grid-relate-dropdown.ng-select-opened ::ng-deep .ng-select-container .ng-arrow-wrapper,
      .fieldmagic-data-grid-relate-dropdown.ng-select-opened ::ng-deep .ng-placeholder,
      .fieldmagic-data-grid-relate-dropdown.ng-select-opened ::ng-deep .ng-value-container .ng-value-label,
      .fieldmagic-data-grid-relate-dropdown.ng-touched ::ng-deep .ng-select-container .ng-clear-wrapper,
      .fieldmagic-data-grid-relate-dropdown.ng-touched ::ng-deep .ng-select-container .ng-arrow-wrapper,
      .fieldmagic-data-grid-relate-dropdown.ng-touched ::ng-deep .ng-value-container .ng-value-label,
      .fieldmagic-data-grid-relate-dropdown.ng-touched ::ng-deep .ng-placeholder {
        top: 6px;
        position: relative;
      }
    `,
  ],
})
export class FieldmagicDataGridRelateDropdownCellInputComponent implements ICellEditorAngularComp {
  relateValue?: OptionalValue<FieldmagicRelateInputValue>;
  textValue?: OptionalValue<string>;

  /// text configs
  max?: OptionalValue<number>;
  readonly remaining$ = new BehaviorSubject<number>(0);
  /// end text configs

  /// don't be an idiot to override this!
  options$: Observable<FieldmagicRelateOption[]>;

  readonly fetching$ = new BehaviorSubject<boolean>(false);

  /// options
  readonly typeahead$ = new Subject<string>();
  readonly placeholder$ = new BehaviorSubject<OptionalValue<string>>(null);
  readonly clearable$ = new BehaviorSubject<boolean>(true);
  readonly multi$ = new BehaviorSubject<boolean>(false);
  readonly tooltip$ = new BehaviorSubject<OptionalValue<string>>(null);
  readonly useText$ = new BehaviorSubject<boolean>(false);
  /// end options

  private readonly _params$ = new BehaviorSubject<OptionalValue<ICellEditorParams>>(null);

  constructor(
    private readonly _relates: RelateSearchService,
  ) {}

  agInit(params: ICellEditorParams): void {
    const metadata: FieldmagicDataGridRelateDropdownInputConfig = Object.assign(
      {
        clearable: true,
        multi: false,
        customizable: false,
      },
      params.colDef.cellEditorParams(params),
    );

    // compute data
    const data = (params.node.group) ? {} : params.node.data;

    if (is_callable(metadata.use_text_when) && metadata.use_text_when(data, params)) {
      this._initTextarea(fallback(metadata.text_config, {
        to: () => ({}),
      }), {
        params,
      });
    } else {
      this._initRelate(metadata, {
        params,
      });
    }

    this._params$.next(params);
  }

  getValue = () => (this.useText$.value) ? data_nullify(str_trim(this.textValue)) : this.relateValue;

  onChanged(): void {
    _update_data_node_value(this.getValue(), {
      params: this._params$.value,
    });

    if (this.useText$.value) {
      this._computeRemaining();
    }
  }

  onOpen(): void {
    this.typeahead$.next('');
  }

  isPopup = () => this.useText$.value;

  private _initRelate(metadata: FieldmagicDataGridRelateDropdownInputConfig, opts: {
    params: ICellEditorParams;
  }): void {
    this.relateValue = data_nullify(opts.params.value);

    this.options$ = concat(
      (filled(this.relateValue)) ? of([this.relateValue]) : of([]),
      this.typeahead$.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((term: string) => observe({
          before: () => this.fetching$.next(true),
          after: () => this.fetching$.next(false),
          observable: () => this._relates.search(metadata.remote_config.module_name, {
            term: term,
            with_additional_filter: fallback(metadata.remote_config.with_additional_server_filter, {
              to: () => true,
            }),
            filter: when<FieldmagicRelateInputFilter>(is_callable(metadata.remote_config.filter), {
              then: () => (metadata.remote_config.filter as CallableFunction)(opts.params.node.data, opts.params.node),
              else: () => metadata.remote_config.filter,
            }),
            record_id: data_get(this.relateValue, 'id'),
          }),
        })),
      ),
    );

    this.tooltip$.next(metadata.tooltip);
    this.placeholder$.next(data_nullify(data_get(metadata, 'placeholder')));
    this.clearable$.next(metadata.clearable);
    this.multi$.next(metadata.multi);
  }

  private _initTextarea(metadata: FieldmagicDataGridTextareaInputConfig, opts: {
    params: ICellEditorParams;
  }): void {
    this.textValue = data_nullify(opts.params.value);

    this.tooltip$.next(metadata.tooltip);
    this.placeholder$.next(metadata.placeholder);

    if (filled(metadata.max_length)) {
      this.max = to_numeric(metadata.max_length);

      this.remaining$.next(difference([
        this.max,
        str_len(this.textValue),
      ]));
    }

    this.useText$.next(true);
  }

  onKeyDown(event: KeyboardEvent) {
    const params = this._params$.value;

    if (blank(params)) {
      return;
    }

    if (event.key === 'Enter' && ! event.shiftKey) {
      params.stopEditing();
    }

    if (event.key === 'Escape') {
      params.stopEditing(true);
    }
  }

  private _computeRemaining(): void {
    const max = this.max;

    if (blank(max) || max < 1) {
      return;
    }

    this.remaining$.next(max - str_len(this.textValue));
  }
}

export type FieldmagicDataGridRelateDropdownInputConfig = FieldmagicDataGridInputConfig & {
  remote_config: {
    module_name: string;
    filter?: FieldmagicRelateInputFilter | ((data: Record<string, any>, node: RowNode) => FieldmagicRelateInputFilter);
    with_additional_server_filter?: boolean;
  };
  editable?: boolean;
  clearable?: boolean;
  multi?: boolean;
  bind_value?: string;
  use_text_when?: (data: Record<string, any>, params: ICellEditorParams) => boolean;
  text_config?: FieldmagicDataGridTextareaInputConfig;
};

@Component({
  selector: `fieldmagic-data-grid-checkbox-cell-input`,
  template: `
    <fieldmagic-checkbox-input
      class="w-100 h-100 d-flex align-items-center justify-content-center"
      [(ngModel)]="value"
      (ngModelChange)="onChanged()"
      [tooltip]="tooltip$ | async"
      [standalone]="true"
    >
    </fieldmagic-checkbox-input>
  `
})
export class FieldmagicDataGridCheckboxCellInputComponent implements ICellEditorAngularComp {
  value?: OptionalValue<boolean> = false;

  readonly id: string = spf('fieldmagic_data_grid_checkbox:%s', {
    args: [generate_id()],
  });

  readonly tooltip$ = new BehaviorSubject<OptionalValue<string>>(null);

  private readonly _params$ = new BehaviorSubject<OptionalValue<ICellEditorParams>>(null);

  agInit(params: ICellEditorParams): void {
    this._params$.next(params);

    const metadata: FieldmagicDataGridCheckboxInputConfig = fallback(params.colDef.cellEditorParams(params), {
      to: () => ({}),
    });

    this.tooltip$.next(metadata.tooltip);

    this.value = data_is_true(params.value);
  }

  getValue = () => this.value;

  onChanged = () => _update_data_node_value(this.value, {
    params: this._params$.value,
  });
}

export type FieldmagicDataGridCheckboxInputConfig = FieldmagicDataGridInputConfig;

@Component({
  selector: 'fieldmagic-data-grid-datetime-cell-input',
  template: `
    <fieldmagic-calendar-input
      class="fieldmagic-data-grid-datetime-cell-input"
      [standalone]="true"
      [(ngModel)]="value"
      [tooltip]="tooltip$ | async"
      (ngModelChange)="onChanged()"
      type="datetime"
    >
    </fieldmagic-calendar-input>
  `,
  styles: [
    `.fieldmagic-data-grid-datetime-cell-input ::ng-deep .fieldmagic-datetime-clear-button {
      top: 0px;
    }`,
  ],
})
export class FieldmagicDataGridDateTimeCellInputComponent implements ICellEditorAngularComp {
  value?: OptionalValue<FieldmagicNativeDateTime>;

  readonly id: string = spf('fieldmagic_data_grid_datetime:%s', {
    args: [generate_id()],
  });

  readonly tooltip$ = new BehaviorSubject<OptionalValue<string>>(null);

  private readonly _params$ = new BehaviorSubject<OptionalValue<ICellEditorParams>>(null);

  agInit(params: ICellEditorParams): void {
    this._params$.next(params);

    const metadata: FieldmagicDataGridCheckboxInputConfig = fallback(params.colDef.cellEditorParams(params), {
      to: () => ({}),
    });

    this.tooltip$.next(metadata.tooltip);

    this.value = data_nullify(params.value);
  }

  getValue = () => this.value;

  onChanged = () => _update_data_node_value(this.value, {
    params: this._params$.value,
  });
}

export type FieldmagicDataGridDateTimeInputConfig = FieldmagicDataGridInputConfig;

@Component({
  selector: 'fieldmagic-data-grid-textarea-cell-input',
  template: `
    <div class="w-100 h-100 d-flex p-2 flex-column">
      <textarea
        rows="10"
        cols="60"
        class="form-control font-size-11 fieldmagic-data-grid-textarea-input"
        [(ngModel)]="value"
        [attr.maxLength]="max"
        [matTooltip]="tooltip$ | async"
        [attr.placeholder]="placeholder$ | async"
        (ngModelChange)="onChanged()"
        (keydown)="onKeyDown($event)"
        autofocus
      >
      </textarea>

      <div
        *ngIf="max | filled"
        class="d-flex w-100 justify-content-end"
      >
        <small class="text-help">
          {{ '%d/%d' | sprintf: [remaining$ | async, max]}}
        </small>
      </div>
    </div>
  `,
})
export class FieldmagicDataGridTextareaCellInputComponent implements ICellEditorAngularComp {
  value?: OptionalValue<string>;
  max?: OptionalValue<number>;

  readonly remaining$ = new BehaviorSubject<number>(0);
  readonly tooltip$ = new BehaviorSubject<OptionalValue<string>>(null);
  readonly placeholder$ = new BehaviorSubject<OptionalValue<string>>(null);

  private readonly _params$ = new BehaviorSubject<OptionalValue<ICellEditorParams>>(null);

  agInit(params: ICellEditorParams): void {
    this._params$.next(params);

    this.value = data_nullify(params.value);

    const metadata: FieldmagicDataGridTextareaInputConfig = fallback(params.colDef.cellEditorParams(params), {
      to: () => ({}),
    });

    this.tooltip$.next(metadata.tooltip);
    this.placeholder$.next(metadata.placeholder);

    if (filled(metadata.max_length)) {
      this.max = to_numeric(metadata.max_length);

      this.remaining$.next(difference([
        this.max,
        str_len(this.value),
      ]));
    }
  }

  getValue = () => data_nullify(str_trim(this.value));

  isPopup = () => true;

  onChanged(): void {
    _update_data_node_value(this.value, {
      params: this._params$.value,
    });

    this._computeRemaining();
  }

  onKeyDown(event: KeyboardEvent) {
    const params = this._params$.value;

    if (blank(params)) {
      return;
    }

    if (event.key === 'Enter' && ! event.shiftKey) {
      params.stopEditing();
    }

    if (event.key === 'Escape') {
      params.stopEditing(true);
    }
  }

  private _computeRemaining(): void {
    const max = this.max;

    if (blank(max) || max < 1) {
      return;
    }

    this.remaining$.next(max - str_len(this.value));
  }
}

export type FieldmagicDataGridTextareaInputConfig = FieldmagicDataGridInputConfig & {
  max_length?: number;
}

const _update_data_node_value = (value: any, opts: {
  params?: OptionalValue<ICellEditorParams>,
} = {}): void => {
  if (blank(opts.params)) {
    return;
  }

  if (! opts.params.node.group) {
    opts.params.node.setDataValue(opts.params.colDef.field, value);
  } else {
    for (const node of opts.params.node.allLeafChildren) {
      /// mass assignment depending on selected node
      if (filled(data_get(node.data, '__selected')) && ! node.isSelected()) {
        continue;
      }

      node.setDataValue(opts.params.colDef.field, value);
    }
  }

  /// refreshes the parent cell to guarantee count updates
  /// as counts are manually computed in the custom group cell renderer
  if (filled(opts.params.node.parent)) {
    opts.params.api.refreshCells({
      rowNodes: [opts.params.node.parent],
      columns: ['ag-Grid-AutoColumn'],
      force: true,
    });
  }
}